import React, { useState, useEffect } from 'react';
import { patents } from '../../data/patents';
import { patents_with_abstracts } from '../../data/patents_with_abstracts';
import {
  PageContainer,
  Header,
  Title,
  Description,
  FilterSection,
  FilterContainer,
  SearchInput,
  Select,
  PatentsSection,
  PatentsGrid,
  PatentCard,
  PatentImage,
  PatentCategory,
  PatentStatus,
  PatentContent,
  PatentTitle,
  PatentId,
  PatentMeta,
  PatentButton,
  PaginationSection,
  PaginationContainer,
  PaginationButton,
  PageInput,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalImage,
  ModalInfo,
  ModalSection,
  GooglePatentsButton
} from './styles';

const PatentsPage = () => {
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [patentsPerPage, setPatentsPerPage] = useState(9);
  const [filteredPatents, setFilteredPatents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedPatent, setSelectedPatent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get all patents
  const getAllPatents = () => {
    return Object.values(patents).flat();
  };

  // Filter patents based on search term, category, and year
  useEffect(() => {
    let filtered = getAllPatents();

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(patent =>
        patent.title.toLowerCase().includes(term) ||
        patent.id.toLowerCase().includes(term) ||
        patent.assignee?.toLowerCase().includes(term) ||
        patent.category?.toLowerCase().includes(term)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(patent => patent.category === selectedCategory);
    }

    if (selectedYear) {
      const year = parseInt(selectedYear);
      filtered = filtered.filter(patent =>
        patent.filingDate && new Date(patent.filingDate).getFullYear() === year
      );
    }

    setFilteredPatents(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedYear]);

  // Get current patents for pagination
  const getCurrentPatents = () => {
    const startIndex = (currentPage - 1) * patentsPerPage;
    const endIndex = startIndex + patentsPerPage;
    return filteredPatents.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredPatents.length / patentsPerPage);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle patent click
  const handlePatentClick = (patent) => {
    setSelectedPatent(patent);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPatent(null);
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get patent abstract
  const getPatentAbstract = (patentId) => {
    const abstractPatent = patents_with_abstracts.medical.find(p => p.id === patentId);
    return abstractPatent?.abstract || 'No abstract available for this patent.';
  };

  return (
    <PageContainer>
      <Header>
        <Title>Patent Portfolio</Title>
        <Description>
          Explore our comprehensive collection of patents across medical devices, display technology, and environmental solutions.
        </Description>
      </Header>

      <FilterSection>
        <FilterContainer>
          <SearchInput
            type="text"
            placeholder="Search patents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            <option value="medical">Medical Devices</option>
            <option value="display">Display Technology</option>
            <option value="environmental">Environmental Solutions</option>
            <option value="other">Other Technologies</option>
          </Select>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="">All Years</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
            <option value="2021">2021</option>
            <option value="2020">2020</option>
          </Select>
          <Select
            value={patentsPerPage}
            onChange={(e) => setPatentsPerPage(parseInt(e.target.value))}
          >
            <option value="9">Show 9 Per Page</option>
            <option value="18">Show 18 Per Page</option>
            <option value="27">Show 27 Per Page</option>
            <option value="36">Show 36 Per Page</option>
            <option value={filteredPatents.length}>Show All</option>
          </Select>
        </FilterContainer>
      </FilterSection>

      <PatentsSection>
        <PatentsGrid>
          {getCurrentPatents().map((patent) => (
            <PatentCard key={patent.id} onClick={() => handlePatentClick(patent)}>
              <PatentImage>
                <img
                  src={patent.image || 'https://via.placeholder.com/400x300?text=No+Image+Available'}
                  alt={patent.title}
                />
                <PatentCategory category={patent.category}>
                  {patent.category.charAt(0).toUpperCase() + patent.category.slice(1)}
                </PatentCategory>
                <PatentStatus status={patent.status}>
                  {patent.status}
                </PatentStatus>
              </PatentImage>
              <PatentContent>
                <PatentTitle>{patent.title}</PatentTitle>
                <PatentId>{patent.id}</PatentId>
                <PatentMeta>
                  <p><span>Assignee:</span> {patent.assignee || 'N/A'}</p>
                  <p><span>Priority Date:</span> {formatDate(patent.priorityDate)}</p>
                  <p><span>Status:</span> {patent.status}</p>
                </PatentMeta>
                <PatentButton onClick={(e) => {
                  e.stopPropagation();
                  handlePatentClick(patent);
                }}>
                  More Details
                </PatentButton>
              </PatentContent>
            </PatentCard>
          ))}
        </PatentsGrid>
      </PatentsSection>

      <PaginationSection>
        <PaginationContainer>
          <PaginationButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </PaginationButton>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>Page</span>
            <PageInput
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => handlePageChange(parseInt(e.target.value))}
            />
            <span>of {totalPages}</span>
          </div>
          <PaginationButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </PaginationButton>
        </PaginationContainer>
      </PaginationSection>

      <Modal isOpen={isModalOpen} onClick={handleCloseModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          {selectedPatent && (
            <>
              <ModalHeader>
                <ModalTitle>{selectedPatent.title}</ModalTitle>
                <CloseButton onClick={handleCloseModal}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <ModalImage
                  src={selectedPatent.image || 'https://via.placeholder.com/400x300?text=No+Image+Available'}
                  alt={selectedPatent.title}
                />
                <ModalInfo>
                  <ModalSection>
                    <h3>Inventors</h3>
                    <p>{selectedPatent.inventors.join(', ')}</p>
                  </ModalSection>
                  <ModalSection>
                    <h3>Assignee</h3>
                    <p>{selectedPatent.assignee}</p>
                  </ModalSection>
                  <ModalSection>
                    <h3>Patent Number</h3>
                    <p>{selectedPatent.id}</p>
                  </ModalSection>
                  <ModalSection>
                    <h3>Dates</h3>
                    <p>
                      Priority Date: {formatDate(selectedPatent.priorityDate)}<br />
                      Filing Date: {formatDate(selectedPatent.filingDate)}<br />
                      Publication Date: {formatDate(selectedPatent.publicationDate)}<br />
                      {selectedPatent.grantDate && `Grant Date: ${formatDate(selectedPatent.grantDate)}`}
                    </p>
                  </ModalSection>
                  <ModalSection>
                    <h3>Abstract</h3>
                    <p>{getPatentAbstract(selectedPatent.id)}</p>
                  </ModalSection>
                  <GooglePatentsButton href={selectedPatent.link} target="_blank">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 22.5C6.21 22.5 1.5 17.79 1.5 12S6.21 1.5 12 1.5 22.5 6.21 22.5 12 17.79 22.5 12 22.5z"/>
                      <path d="M12 3C7.029 3 3 7.029 3 12s4.029 9 9 9 9-4.029 9-9S16.971 3 12 3zm0 16.5c-4.136 0-7.5-3.364-7.5-7.5S7.864 4.5 12 4.5s7.5 3.364 7.5 7.5-3.364 7.5-7.5 7.5z"/>
                    </svg>
                    View on Google Patents
                  </GooglePatentsButton>
                </ModalInfo>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default PatentsPage; 